/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

import moment from 'moment-timezone';

/**
 * Function which calculates the difference in days
 * between 2 dates
 * @param {String} startDate
 * @param {String} endDate
 * @return {Number}
 */
export const dateDiffInDays = (startDate, endDate) => {
  const sDate = moment(startDate);
  const eDate = moment(endDate);
  const diff = eDate.diff(sDate, 'days');
  return diff;
};
/**
* Returns the UTC date, modified by adding or subtracting an offset of days.
* Default offset is 0 (i.e current date).
* @param {*} daysOffset
*/
export const getDate = (daysOffset = 0) => {
  const today = new Date();
  return new Date(new Date().setDate(today.getDate() + daysOffset)).getTime();
};

/**
 *A function used
 *
 * @param {Object} period
 * @param {String} orgTimezone
 * It's form is :
 * {
 * gte: Moment {_isAMomentObject: true, _i: 1637067669999, _isUTC: true, _pf: {…}, _locale: Locale, …}
 * lte: Moment {_isAMomentObject: true, _i: 1637067669999, _isUTC: true, _offset: 0, _pf: {…}, …}
 * }
 * @return {Object}
 */
export const getPreviousPeriodObject = (period, orgTimezone) => {
  const daysOffset = dateDiffInDays(period.gte, period.lte);
  const daysToToday = dateDiffInDays(period.gte, new Date());
  return {
    gte: moment.tz(getDate(-(daysToToday + daysOffset)), orgTimezone).startOf('day').format(),
    lte: moment.tz(getDate(-(daysToToday)), orgTimezone).endOf('day').format()
  };
};

/**
 * Get the last 30 days period
 * @param {String} orgTimezone
 * {
 * gte: Moment {_isAMomentObject: true, _i: 1637067669999, _isUTC: true, _pf: {…}, _locale: Locale, …}
 * lte: Moment {_isAMomentObject: true, _i: 1637067669999, _isUTC: true, _offset: 0, _pf: {…}, …}
 * }
 * @return {Object}
 */
export const getCurrentPeriod = (orgTimezone='Etc/UTC') => ({
  gte: moment.tz(getDate(-30), orgTimezone).startOf('day').format(),
  lte: moment.tz(getDate(), orgTimezone).endOf('day').format()
});

/**
 * Get the 30 days period before the last 30 days
 * @param {String} orgTimezone
 * {
 * gte: Moment {_isAMomentObject: true, _i: 1637067669999, _isUTC: true, _pf: {…}, _locale: Locale, …}
 * lte: Moment {_isAMomentObject: true, _i: 1637067669999, _isUTC: true, _offset: 0, _pf: {…}, …}
 * }
 * @return {Object}
 */
export const getPreviousPeriod = (orgTimezone='Etc/UTC') => ({
  gte: moment.tz(getDate(-60), orgTimezone).startOf('day').format(),
  lte: moment.tz(getDate(-30), orgTimezone).endOf('day').format()
});

/**
 * Instantiate a current 30 period object
 * {
 * gte: Moment {_isAMomentObject: true, _i: 1637067669999, _isUTC: true, _pf: {…}, _locale: Locale, …}
 * lte: Moment {_isAMomentObject: true, _i: 1637067669999, _isUTC: true, _offset: 0, _pf: {…}, …}
 * }
 */
export const current_period = {
  gte: moment.tz(getDate(-30), 'Etc/UTC').startOf('day').format(),
  lte: moment.tz(getDate(), 'Etc/UTC').endOf('day').format()
};

/**
 * Instantiate a previous 30 period object
 * {
 * gte: Moment {_isAMomentObject: true, _i: 1637067669999, _isUTC: true, _pf: {…}, _locale: Locale, …}
 * lte: Moment {_isAMomentObject: true, _i: 1637067669999, _isUTC: true, _offset: 0, _pf: {…}, …}
 * }
 */
export const previous_period = {
  gte: moment.tz(getDate(-60), 'Etc/UTC').startOf('day').format(),
  lte: moment.tz(getDate(-30), 'Etc/UTC').endOf('day').format()
};

/**
 * This function calculates the difference between the current time and the
 * originTimestamp.
 * @param {*} originTimestamp the timestamp to calculate the difference from
 * @param {*} orgTimezone  he timezone of the originTimestamp
 * @returns an object containing the time difference to be displayed, and a more detailed time, containing
 * also the date, hour and timezone.
 * @example getTimeDifference('2021-11-16T15:00:00.000Z', 'Etc/UTC') // returns {'15:00', 'Aug 22 2024, 15:00 Europe/Athens'}
 * @example getTimeDifference('2021-11-16T15:00:00.000Z', 'Etc/UTC') // returns {'Yesterday', 'Aug 21 2024, 12:00 Europe/Athens'}
 * @example getTimeDifference('2021-11-16T15:00:00.000Z', 'Etc/UTC') // returns {'Nov 16 2021', '10:00 Europe/Athens'}
 */
export function getTimeDifference(originTimestamp, browserTimezone = 'Etc/UTC') {
  const originTimestampTimezoned = moment.utc(originTimestamp).tz(browserTimezone);
  const now = moment().tz(browserTimezone);

  // Check if the notification was created yesterday
  const startOfToday = now.startOf('day');
  const startOfYesterday = startOfToday.clone().subtract(1, 'day');
  if (originTimestampTimezoned.isBetween(startOfYesterday, startOfToday, null, '[)')) {
    return {
      displayTime: 'Yesterday',
      detailedTime: originTimestampTimezoned.format(`YYYY-MM-DD HH:mm:ss [${browserTimezone}]`)
    };
  }

  // Find out if the difference should be displayed in hours (within the past 24 hours)
  const hoursDiff = now.diff(originTimestampTimezoned, 'hours');
  if (hoursDiff < 24) {
    return {
      displayTime: originTimestampTimezoned.format('HH:mm'),
      detailedTime: originTimestampTimezoned.format(`YYYY-MM-DD [${browserTimezone}]`)
    };
  }

  return {
    // Jan 1 2024
    displayTime: originTimestampTimezoned.format('MMM D YYYY'),
    detailedTime: originTimestampTimezoned.format(`YYYY-MM-DD HH:mm:ss [${browserTimezone}]`)
  };
}
